import { alpha, Box, Drawer, Tab } from "@mui/material";
import React, { createElement, FunctionComponent, SyntheticEvent, useEffect, useState } from "react";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import makeStyles from "@mui/styles/makeStyles";
import { ArrowLeft, ArrowRight } from "@mui/icons-material";

const useScriptStyles = makeStyles(theme => ({
    dragger: {
        backgroundColor: alpha(theme.palette.common.black, 0.05),
        top: 0,
        right: 0,
        bottom: 0,
        width: 12,
        position: "absolute",
        "& svg": {
            width: 22,
            height: 22,
            left: -5,
            position: "absolute",
            top: "50%",
            cursor: "pointer"
        }
    },
    drawerPaper: {
        overflow: "unset",
        height: "100%"
    },
    tabContent: {
        maxWidth: "100%",
        height: "100%",
        marginTop: 64,
        display: "flex",
        flexDirection: "column"
    },
    tabList: {
        "& .MuiButtonBase-root ": {
            padding: theme.spacing(0, 1),
            margin: 0
        }
    },
    tabs: {
        display: 'flex',
        flexDirection: "column",
        height: "100%",
    },
    tabPanels: {
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        overflowY: "auto",
        marginBottom: "64px"
    },
    tabPanel: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        padding: theme.spacing(1),
        paddingRight: theme.spacing(2.5)
    }
}));

export interface ISideBarTab
{
    name: string
    type: FunctionComponent<any>
}
interface IProps
{
    onSetWidth?: (width: number) => void
    tabs: ISideBarTab[]
}
export function SideBar({ onSetWidth, tabs }: IProps)
{
    const classes = useScriptStyles();
    const [dragging, setDragging] = useState(false);
    const [selected, setSelected] = useState(tabs.length ? tabs[0].name : "");
    const [width, setWidth] = useState(parseInt(window.localStorage.getItem("sideBar.width") || "250"));
    const [open, setOpen] = useState((window.localStorage.getItem("sideBar.open") ?? "1") === "1");
    const minWidth = 12;
    const handleTabChange = (event: SyntheticEvent, value: any) => setSelected(value);
    const handleMouseDown = () =>
    {
        setDragging(true)
    };

    useEffect(() =>
    {
        const handleMouseMove = ({ clientX }: MouseEvent) =>
        {
            if (dragging && open)
            {
                const width = Math.max(clientX, minWidth);

                setWidth(width);
            }
        };

        const handleMouseUp = () =>
        {
            if (dragging)
            {
                if (width.toString() !== window.localStorage.getItem("sideBar.width"))
                {
                    window.localStorage.setItem("sideBar.width", width.toString());
                }
                else
                {
                    setOpen(!open);
                    window.localStorage.setItem("sideBar.open", !open ? "1" : "0");
                }
                setDragging(false);
            }
        }

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () =>
        {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [dragging, width, open]);
    useEffect(() => {
        onSetWidth && onSetWidth(width);
    }, [onSetWidth, width])
    const activeWidth = open ? width : minWidth;
    return (
        <Drawer classes={{ paper: classes.drawerPaper }} variant="permanent" open={true} sx={{ width: activeWidth }}>
            <Box className={classes.tabContent} sx={{ width: activeWidth }}>
                <Box className={classes.dragger} sx={{ cursor: open ? "ew-resize" : "pointer" }} draggable
                    onMouseDown={handleMouseDown} onDragStart={(e) => e.preventDefault()}>
                    {open ? <ArrowLeft /> : <ArrowRight />}
                </Box>
                    { open && 
                        <TabContext value={selected}>
                            <TabList onChange={handleTabChange} variant={"scrollable"} className={classes.tabList} sx={{ m: 1 }}>
                                {tabs.map((tab, index) =>
                                    <Tab key={index} label={tab.name} value={tab.name} wrapped />)}
                            </TabList>
                            <Box className={classes.tabPanels}>
                                {tabs.filter(tab => selected === tab.name)
                                    .map((tab, index) =>
                                        <TabPanel className={classes.tabPanel} key={index} value={tab.name}>
                                            {createElement(tab.type)}
                                        </TabPanel>
                                    )}
                            </Box>
                        </TabContext>
                    }
            </Box>
        </Drawer>
    );
} 